"use strict";
/*
 * Membrane React package
 * {@link https://www.npmjs.com/package/@membranehq/react}
 * v0.29.2
 */
'use client';

import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { getSchemaFromValue, getReferenceCollectionPointerForSchema } from '@membranehq/sdk';
import { EditorTagBadge, EditorTagBadgeIcon } from './EditorTagBadge/index.mjs';
import { SvgIcon } from '../../SvgIcon/index.mjs';
import { SvgIconType } from '../../SvgIcon/svg-icon-type.mjs';
import { useMembrane } from '../../../contexts/use-integration-app.mjs';
import { useIntegrationAppConnection } from '../../../contexts/use-integration-app-connection.mjs';
import { forwardRef } from 'react';
import useSWR from 'swr';
import { Truncate } from '../../Truncate/index.mjs';
import { CUSTOM_ARRAY_VALUE_LABEL, CUSTOM_OBJECT_VALUE_LABEL } from '../options-factories/custom-field/constants.mjs';

const ComboBoxSchemaValueBadge = forwardRef(
  ({ value, schema: providedSchema, variablesSchema, className, disabled, ...props }, ref) => {
    let valueElement;
    const schema = providedSchema?.type ? providedSchema : getSchemaFromValue(value, variablesSchema);
    const refCollection = getReferenceCollectionPointerForSchema(schema);
    if (refCollection) {
      return /* @__PURE__ */ jsx(ReferenceCollectionValue, { schema, value, onDelete: props.onDelete });
    }
    if (schema?.referenceRecords) {
      const selectedRecord = schema.referenceRecords.find((r) => r.id == value);
      if (selectedRecord?.name) {
        valueElement = /* @__PURE__ */ jsx(Truncate, { children: selectedRecord.name });
      }
    }
    if (!valueElement) {
      valueElement = getChildrenForSchemaType(schema, value);
    }
    if (valueElement) {
      const icon = getIconForSchemaType(schema);
      return /* @__PURE__ */ jsxs(EditorTagBadge, { className, ref, disabled, variant: "static-value", ...props, children: [
        icon && /* @__PURE__ */ jsx(EditorTagBadgeIcon, { type: icon }),
        valueElement
      ] });
    } else {
      return /* @__PURE__ */ jsxs(EditorTagBadge, { className, ref, disabled, variant: "static-value", ...props, children: [
        /* @__PURE__ */ jsx(EditorTagBadgeIcon, { type: SvgIconType.QuestionMark }),
        "Unknown"
      ] });
    }
  }
);
ComboBoxSchemaValueBadge.displayName = "ComboBoxSchemaValue";
function getChildrenForSchemaType(schema, value) {
  switch (schema?.type) {
    case "boolean":
      return value === void 0 ? void 0 : value ? /* @__PURE__ */ jsx(Fragment, { children: "Yes" }) : /* @__PURE__ */ jsx(Fragment, { children: "No" });
    case "object":
      if (Object.keys(schema?.properties ?? {}).length === 0) {
        return /* @__PURE__ */ jsx(Truncate, { children: CUSTOM_OBJECT_VALUE_LABEL });
      }
      break;
    case "array":
      return /* @__PURE__ */ jsx(Truncate, { children: CUSTOM_ARRAY_VALUE_LABEL });
    case "string":
    case "number":
    case "integer":
    default:
      if (value !== void 0) {
        return /* @__PURE__ */ jsx(Truncate, { children: value?.toString() });
      }
  }
  return void 0;
}
function getIconForSchemaType(schema) {
  switch (schema?.type) {
    case "string":
      return SvgIconType.SchemaType_string;
    case "boolean":
      return SvgIconType.SchemaType_boolean;
    case "number":
    case "integer":
      return SvgIconType.Braces;
    default:
      return void 0;
  }
}
function ReferenceCollectionValue({
  value,
  schema,
  onDelete
}) {
  const client = useMembrane();
  const { connectionId } = useIntegrationAppConnection();
  const strValue = value?.toString() ?? "";
  const key = `reference-value-${connectionId}-${strValue}`;
  const pointer = getReferenceCollectionPointerForSchema(schema);
  const { data: findResponse, isLoading } = useSWR(
    client && connectionId ? key : void 0,
    async () => {
      try {
        return await client.connection(connectionId).dataCollection(pointer.key, pointer.parameters).findById({ id: String(value) });
      } catch {
        return void 0;
      }
    }
  );
  const record = findResponse?.record;
  if (isLoading) {
    return /* @__PURE__ */ jsxs(EditorTagBadge, { variant: "value", onDelete, children: [
      /* @__PURE__ */ jsx("span", { children: record?.name || strValue }),
      /* @__PURE__ */ jsx(SvgIcon, { type: SvgIconType.Loader, className: "w-3 aspect-square" })
    ] });
  }
  if (record?.name) {
    return /* @__PURE__ */ jsx(EditorTagBadge, { variant: "value", onDelete, children: record.name });
  }
  return /* @__PURE__ */ jsx(EditorTagBadge, { variant: "value", onDelete, children: strValue });
}

export { ComboBoxSchemaValueBadge };
